import type { BoxlessDropoffPricing } from "@redotech/admin-sdk/rpc/schema/integrations/boxless-dropoff-fees";
import { DropoffPricingMode } from "@redotech/congo-enums/dropoff-pricing-mode";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { DropoffProviderType } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { BoxlessDropoffsValue, MarkupType } from "./boxless-dropoffs-settings";

export function useBoxlessDropoffs() {
  const { teamId } = useParams();
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [reloadSymbol, setReloadSymbol] = useState(Symbol());

  const { value: data, error } = useLoad(
    async (signal) => {
      if (!teamId) {
        return undefined;
      }
      const { fees } = await rpcClient.getBoxlessDropoffFees(
        { teamId },
        { signal },
      );
      return { teamId, fees };
    },
    [rpcClient, teamId, reloadSymbol],
  );

  const refresh = useHandler(() => setReloadSymbol(Symbol()));

  // A load keeps its previous value while `teamId` changes, so the fees are
  // only this team's once they arrive stamped with it.
  const fees = data && data.teamId === teamId ? data.fees : undefined;

  const initial = useMemo<BoxlessDropoffsValue>(
    () =>
      (fees ?? [])
        .filter((fee) => fee.available)
        .map(({ provider, pricing }) => ({
          provider,
          mode: pricing.mode,
          // A write stores one of the two; a pair would read as a percentage.
          markupType:
            pricing.mode === DropoffPricingMode.Markup &&
            (pricing.percent ?? 0) > 0
              ? MarkupType.Percent
              : MarkupType.Flat,
          amount: String(
            pricing.mode === DropoffPricingMode.Fixed ? pricing.amount : 0,
          ),
          currency:
            pricing.mode === DropoffPricingMode.Fixed
              ? pricing.currency
              : Currency.USD,
          markupAmount: String(
            pricing.mode === DropoffPricingMode.Markup
              ? (pricing.amount ?? 0)
              : 0,
          ),
          markupPercent: String(
            pricing.mode === DropoffPricingMode.Markup
              ? (pricing.percent ?? 0)
              : 0,
          ),
        })),
    [fees],
  );

  const updatePricing = useHandler(
    async (
      provider: DropoffProviderType,
      pricing: BoxlessDropoffPricing,
      signal: AbortSignal,
    ) => {
      if (!teamId) {
        return;
      }
      await rpcClient.updateBoxlessDropoffFee(
        { teamId, provider, pricing },
        { signal },
      );
    },
  );

  return { teamId, initial, loaded: !!fees, error, updatePricing, refresh };
}
